import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import logoImage from '../assets/logo_site.png?inline';
import { PROXY_SERVERS } from '../config/proxy';
import { PlugIcon } from './Icons';
import { buildTelegramAppLink, normalizeTelegramWebLink } from '../utils/telegramLinks';

const hasMultiple = PROXY_SERVERS.length > 1;

function Proxy() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  // Reset copy state when switching servers
  useEffect(() => {
    setCopied(false);
  }, [activeIndex]);

  const activeServer = PROXY_SERVERS[activeIndex];
  const activeServerOnline = activeServer?.isOnline !== false;
  const activeServerLink = buildTelegramAppLink(activeServer?.link);
  const activeServerDetails = activeServer
    ? (() => {
        try {
          const parsedUrl = new URL(normalizeTelegramWebLink(activeServer.link));
          const params = parsedUrl.searchParams;
          const protocol = (parsedUrl.hostname || activeServer.protocol || '—').toLowerCase();
          const server = params.get('server') || '—';
          const port = params.get('port') || activeServer.port || '—';

          if (protocol === 'socks') {
            const user = params.get('user');
            const pass = params.get('pass');
            const secret = user || pass
              ? [user, pass].filter(Boolean).join(' / ')
              : 'Не требуется';

            return { server, port, secret };
          }

          return {
            server,
            port,
            secret: params.get('secret') || '—',
          };
        } catch {
          return {
            server: '—',
            port: activeServer.port || '—',
            secret: '—',
          };
        }
      })()
    : {
        server: '—',
        port: '—',
        secret: '—',
      };

  const handleCopy = useCallback(async () => {
    if (!activeServer) return;
    try {
      await navigator.clipboard.writeText(activeServerLink);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = activeServerLink;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  }, [activeServer, activeServerLink]);

  if (PROXY_SERVERS.length === 0) {
    return (
      <section className={`proxy-page ${isVisible ? 'proxy-page--visible' : ''}`}>
        <div className="proxy-server-card proxy-server-card--empty">
          <div className="proxy-server-card__header">
            <span className="proxy-server-card__label">ПРОКСИ-СЕРВЕР</span>
          </div>
          <div className="proxy-server-card__info" style={{ justifyContent: 'center', textAlign: 'center', padding: '2rem 0' }}>
            <div className="proxy-server-card__details">
              <h1 className="proxy-server-card__name" style={{ color: 'var(--accent1)' }}>Нет доступных прокси</h1>
              <p className="proxy-server-card__desc" style={{ marginTop: '0.5rem', color: '#929aaa' }}>
                В данный момент все серверы находятся на техническом обслуживании
              </p>
            </div>
          </div>
          <Link to="/" className="proxy-cta-btn" style={{ background: 'var(--accent1)', color: 'var(--text-dark)', border: 'none', fontWeight: 'bold' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(180deg)' }}>
              <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
            </svg>
            Вернуться на главную
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className={`proxy-page ${isVisible ? 'proxy-page--visible' : ''}`}>

      {/* === Server Card === */}
      <div className="proxy-server-card">
        <div className="proxy-server-card__header">
          <span className="proxy-server-card__label">ПРОКСИ-СЕРВЕР</span>
        </div>

        <div className="proxy-server-card__info">
          <span className="proxy-server-card__logo-frame">
            <img
              src={logoImage}
              alt="Protocol GOIDA"
              width="56"
              height="56"
              loading="eager"
            />
          </span>
          <div className="proxy-server-card__details">
            <h1 className="proxy-server-card__name">{activeServer.name}</h1>
            <div className="proxy-server-card__status">
              <span className={`proxy-status-dot ${activeServerOnline ? '' : 'proxy-status-dot--offline'}`} />
              <span className={`proxy-status-text ${activeServerOnline ? '' : 'proxy-status-text--offline'}`}>
                {activeServerOnline ? 'Онлайн' : 'Недоступен'}
              </span>
            </div>
          </div>
        </div>

        {/* Server Selector (only when multiple) */}
        {hasMultiple && (
          <div className="proxy-selector">
            {PROXY_SERVERS.map((server, i) => (
              <button
                key={server.id}
                type="button"
                className={`proxy-selector__item ${server.isOnline !== false ? 'proxy-selector__item--online' : 'proxy-selector__item--offline'} ${i === activeIndex ? 'proxy-selector__item--active' : ''}`}
                onClick={() => setActiveIndex(i)}
              >
                <span className="proxy-selector__dot" />
                <span className="proxy-selector__name">{server.name}</span>
              </button>
            ))}
          </div>
        )}

        <a
          href={activeServerLink}
          className="proxy-cta-btn"
        >
          <span className="proxy-cta-btn__icon" aria-hidden="true">
            <PlugIcon className="proxy-cta-btn__plug-svg" />
          </span>
          Подключить в Telegram
        </a>

        <div className="proxy-server-card__divider" />

        <details className="proxy-details">
          <summary className="proxy-details__summary">
            Подробности подключения
            <svg className="proxy-details__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </summary>
          <div className="proxy-details__content">
            <div className="proxy-details__row">
              <span className="proxy-details__label">Сервер</span>
              <span className="proxy-details__value">{activeServerDetails.server}</span>
            </div>
            <div className="proxy-details__row">
              <span className="proxy-details__label">Порт</span>
              <span className="proxy-details__value">{activeServerDetails.port}</span>
            </div>
            <div className="proxy-details__row proxy-details__row--secret">
              <span className="proxy-details__label">Ключ</span>
              <span className="proxy-details__value proxy-details__value--secret">{activeServerDetails.secret}</span>
            </div>
            <div className="proxy-details__row">
              <span className="proxy-details__label">Статус</span>
              <span className={`proxy-details__value ${activeServerOnline ? 'proxy-details__value--online' : 'proxy-details__value--offline'}`}>
                <span className={`proxy-status-dot proxy-status-dot--sm ${activeServerOnline ? '' : 'proxy-status-dot--offline'}`} />
                {activeServerOnline ? 'Активен' : 'Недоступен'}
              </span>
            </div>
          </div>
        </details>
      </div>

      {/* === How to Connect === */}
      <div className="proxy-steps-card">
        <h2 className="proxy-steps-card__title">КАК ПОДКЛЮЧИТЬ</h2>
        <div className="proxy-steps">
          <div className="proxy-step">
            <span className="proxy-step__number">1</span>
            <p className="proxy-step__text">
              {hasMultiple
                ? <>Выберите нужный сервер, затем нажмите <strong>«Подключить в Telegram»</strong> — откроется приложение</>
                : <>Нажмите кнопку <strong>«Подключить в Telegram»</strong> — откроется приложение</>
              }
            </p>
          </div>
          <div className="proxy-step">
            <span className="proxy-step__number">2</span>
            <p className="proxy-step__text">
              Нажмите <strong>«Добавить прокси»</strong> в диалоге Telegram
            </p>
          </div>
          <div className="proxy-step">
            <span className="proxy-step__number">3</span>
            <p className="proxy-step__text">
              Готово. Прокси активирован — пользуйтесь <strong>без ограничений</strong>
            </p>
          </div>
        </div>

        <div className="proxy-steps-note">
          <div className="proxy-steps-note__title">ВАЖНО</div>
          <div className="proxy-steps-note__item">
            <span className="proxy-steps-note__mark proxy-steps-note__mark--ok">+</span>
            <span>Прокси <strong>работает без VPN</strong></span>
          </div>
          <div className="proxy-steps-note__item">
            <span className="proxy-steps-note__mark proxy-steps-note__mark--ok">+</span>
            <span>Прокси <strong>может работать одновременно с нашим VPN</strong></span>
          </div>
          <div className="proxy-steps-note__item">
            <span className="proxy-steps-note__mark proxy-steps-note__mark--warn">−</span>
            <span>
              <strong>Звонки через прокси недоступны</strong>
              <span className="proxy-steps-note__hint">Для звонков лучше использовать наш VPN — с ним Telegram работает полностью.</span>
            </span>
          </div>
        </div>
      </div>

      {/* === Share with Friends === */}
      <div className="proxy-steps-card">
        <h2 className="proxy-steps-card__title">ПОДЕЛИТЬСЯ С ДРУЗЬЯМИ</h2>
        <div className="proxy-steps">
          <div className="proxy-step">
            <span className="proxy-step__number">1</span>
            <p className="proxy-step__text">
              Нажмите <strong>«Скопировать ссылку»</strong> — ссылка окажется в буфере обмена
            </p>
          </div>
          <div className="proxy-step">
            <span className="proxy-step__number">2</span>
            <p className="proxy-step__text">
              Отправьте её другу любым удобным способом — в Telegram, WhatsApp, SMS или просто скиньте в чат
            </p>
          </div>
          <div className="proxy-step">
            <span className="proxy-step__number">3</span>
            <p className="proxy-step__text">
              Другу достаточно <strong>нажать на ссылку</strong> — Telegram откроется и предложит добавить прокси автоматически
            </p>
          </div>
        </div>

        <div className="proxy-share">
          {hasMultiple && (
            <span className="proxy-share__server-label">{activeServer.name}</span>
          )}
          <div className="proxy-share__link-box">
            <span className="proxy-share__link-text">{activeServerLink}</span>
          </div>
          <button
            className={`proxy-share__copy-btn ${copied ? 'proxy-share__copy-btn--copied' : ''}`}
            onClick={handleCopy}
            type="button"
          >
            {copied ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Скопировано!
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
                Скопировать
              </>
            )}
          </button>
        </div>

        <p className="proxy-share__hint">
          Ссылка открывает прокси прямо в Telegram — другу не нужно ничего вводить вручную.
        </p>
      </div>
    </section>
  );
}

export default Proxy;
